import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Printer, Banknote, CreditCard, Phone, Building2, HandCoins, ArrowLeft } from "lucide-react";
import { api, apiError, conflictMessage } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState, StatusBadge } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem, MotionButton, DelightBurst } from "@/components/motion";
import { useOperationalSync } from "@/hooks/useOperationalSync";

const METHODS = [
  { id: "cash", icon: Banknote, key: "cash" },
  { id: "card", icon: CreditCard, key: "card" },
  { id: "mobile", icon: Phone, key: "mobile" },
  { id: "bank", icon: Building2, key: "bank" },
  { id: "debt", icon: HandCoins, key: "debt" },
];

export default function Cashier() {
  const { t, money, lang } = useI18n();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState({});
  const [settings, setSettings] = useState({});
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [servicePercent, setServicePercent] = useState(0);
  const [received, setReceived] = useState("");
  const [method, setMethod] = useState("cash");
  const [debtorName, setDebtorName] = useState("");
  const [debtorPhone, setDebtorPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [payDelight, setPayDelight] = useState(0);
  const [ordersLoaded, setOrdersLoaded] = useState(false);

  const load = useCallback(async () => {
    const [r1, r2, r3] = await Promise.all([
      api.get("/orders", { params: { payment_status: "unpaid", limit: 300 } }),
      api.get("/tables", { params: { limit: 500 } }),
      api.get("/settings"),
    ]);
    setOrders(r1.data);
    setTables(r2.data.reduce((m, t) => ({ ...m, [t.id]: t }), {}));
    setSettings(r3.data || {});
    if (r3.data) {
      setTaxPercent(r3.data.tax_enabled ? Number(r3.data.tax_percent || 0) : 0);
      setServicePercent(r3.data.service_enabled ? Number(r3.data.service_percent || 0) : 0);
    }
    setOrdersLoaded(true);
  }, []);

  useOperationalSync(load, { pollMs: 8000 });

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.order_number.toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      String(tables[o.table_id]?.number || "").includes(q);
  });

  const subtotal = selected?.subtotal || 0;
  const discount = useMemo(() => {
    if (discountType === "percent") return subtotal * (Number(discountValue) / 100);
    if (discountType === "fixed") return Number(discountValue);
    return 0;
  }, [discountType, discountValue, subtotal]);
  const afterDisc = Math.max(0, subtotal - discount);
  const taxAmt = afterDisc * (Number(taxPercent) / 100);
  const serviceAmt = afterDisc * (Number(servicePercent) / 100);
  const total = afterDisc + taxAmt + serviceAmt;
  const recv = Number(received || 0);
  const change = recv > total ? recv - total : 0;
  const remaining = recv < total ? total - recv : 0;

  const pickOrder = (o) => {
    setSelected(o);
    setDiscountType("none");
    setDiscountValue(0);
    setReceived(String(o.subtotal));
    setMethod("cash");
  };

  const submit = async () => {
    if (!selected || submitting) return;
    const amt = method === "debt" ? 0 : recv;
    setSubmitting(true);
    const idempotencyKey = `pay-${selected.id}-${crypto.randomUUID()}`;
    try {
      const r = await api.post("/payments", {
        order_id: selected.id,
        amount: amt,
        method,
        discount_type: discountType,
        discount_value: Number(discountValue),
        tax_percent: Number(taxPercent),
        service_percent: Number(servicePercent),
        debtor_name: debtorName,
        debtor_phone: debtorPhone,
        idempotency_key: idempotencyKey,
      });
      if (r.data?.idempotent) {
        toast.info(lang === "pashto" ? "دا پیسې مخکې ثبت شوي" : "این پرداخت قبلاً ثبت شده بود");
      } else {
        toast.success(t("success"));
        setPayDelight((n) => n + 1);
      }
      window.open(`/receipt/${selected.id}`, "_blank");
      setSelected(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(conflictMessage(e, t("alreadyPaid") || "این سفارش قبلاً تسویه شده است"));
        load();
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4 lg:gap-6 lg:h-[calc(100vh-9rem)]">
      {/* Orders List (right) */}
      <div className="lg:col-span-2 rp-card p-4 flex flex-col min-h-0">
        <h2 className="text-lg font-bold font-heading mb-3 text-gray-900">{t("activeOrders")}</h2>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="cashier-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search")}
            className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]"
          />
        </div>
        <div className="space-y-2 overflow-y-auto thin-scroll flex-1 -mx-1 px-1">
          {filtered.length === 0 && <EmptyState title={t("empty")} />}
          <StaggerRoot className="space-y-2" delay={0.03}>
          {ordersLoaded && filtered.map((o, idx) => (
            <StaggerItem key={o.id} index={idx}>
            <button
              data-testid={`cashier-order-${o.order_number}`}
              onClick={() => pickOrder(o)}
              className={`w-full text-right rp-card p-3 transition-all ${selected?.id === o.id ? "border-[#071A33] ring-2 ring-[#071A33]/20" : "hover:border-gray-300"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-500 font-mono">#{o.order_number}</div>
                  <div className="font-bold text-gray-900 text-base mt-0.5">
                    {tables[o.table_id] ? `${t("table")} ${tables[o.table_id].number}` : t(o.order_type === "takeaway" ? "takeaway" : "delivery")}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-lg font-bold tabular text-[#071A33]">{money(o.subtotal)}</div>
                  <StatusBadge status={o.status} label={t(o.status) || o.status} />
                </div>
              </div>
            </button>
            </StaggerItem>
          ))}
          </StaggerRoot>
        </div>
      </div>

      {/* Payment Panel (left) */}
      <div className="lg:col-span-3 rp-card p-4 md:p-5 flex flex-col min-h-0 relative">
        <DelightBurst trigger={payDelight} />
        {!selected ? (
          <div className="flex-1 grid place-items-center">
            <EmptyState icon={Banknote} title={lang === "pashto" ? "د تادیې لپاره یو فرمایش وټاکئ" : "برای پرداخت یک سفارش انتخاب کنید"} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold font-heading text-gray-900">
                #{selected.order_number}
              </h2>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 text-sm">
                <ArrowLeft className="w-4 h-4" />
                {t("cancel")}
              </button>
            </div>

            <div className="overflow-y-auto thin-scroll flex-1 space-y-2 mb-3">
              {selected.items.map((it) => (
                <div key={it.id} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {lang === "pashto" ? it.name_snapshot_pashto : it.name_snapshot_dari}
                    </div>
                    <div className="text-xs text-gray-500 tabular">{it.quantity} × {money(it.unit_price)}</div>
                  </div>
                  <div className="font-bold tabular text-gray-900">{money(it.total_price)}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2">
              <RpSelect
                testId="discount-type"
                value={discountType}
                onValueChange={setDiscountType}
                className="h-11"
                options={[
                  { value: "none", label: `${t("discount")}: —` },
                  { value: "fixed", label: `${t("discount")}: ${lang === "pashto" ? "ثابت" : "ثابت"}` },
                  { value: "percent", label: `${t("discount")}: %` },
                ]}
              />
              <input
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                type="number"
                disabled={discountType === "none"}
                className="h-11 px-3 rounded-xl border border-gray-200 text-sm tabular disabled:bg-gray-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rp-card p-2 px-3 text-sm">
                <span className="text-gray-500">{t("tax")} %</span>
                <input
                  type="number"
                  value={taxPercent}
                  onChange={(e) => setTaxPercent(e.target.value)}
                  className="ml-2 w-16 bg-transparent text-right tabular font-medium focus:outline-none"
                />
              </div>
              <div className="rp-card p-2 px-3 text-sm">
                <span className="text-gray-500">{t("service")} %</span>
                <input
                  type="number"
                  value={servicePercent}
                  onChange={(e) => setServicePercent(e.target.value)}
                  className="ml-2 w-16 bg-transparent text-right tabular font-medium focus:outline-none"
                />
              </div>
            </div>

            <div className="rp-card p-3 mb-3 space-y-1 text-sm">
              <Row label={t("subtotal")} value={money(subtotal)} />
              <Row label={t("discount")} value={"-" + money(discount)} red={discount > 0} />
              <Row label={t("tax")} value={money(taxAmt)} />
              <Row label={t("service")} value={money(serviceAmt)} />
              <div className="border-t border-gray-100 pt-2 mt-2 flex justify-between font-bold">
                <span>{t("total")}</span>
                <span className="text-xl tabular text-[#071A33]">{money(total)}</span>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-2 mb-3">
              {METHODS.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    data-testid={`method-${m.id}`}
                    onClick={() => { setMethod(m.id); if (m.id === "debt") setReceived("0"); }}
                    className={`p-3 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 ${method === m.id ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
                  >
                    <Icon className="w-5 h-5" />
                    {t(m.key)}
                  </button>
                );
              })}
            </div>

            {method !== "debt" && (
              <div className="mb-3">
                <label className="text-sm text-gray-600 mb-1 block">{t("receivedAmount")}</label>
                <input
                  data-testid="received-amount"
                  value={received}
                  onChange={(e) => setReceived(e.target.value)}
                  type="number"
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 text-lg tabular font-bold focus:border-[#071A33] focus:outline-none"
                />
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-emerald-600">{t("change")}: <span className="font-bold tabular">{money(change)}</span></span>
                  <span className="text-rose-600">{t("remaining")}: <span className="font-bold tabular">{money(remaining)}</span></span>
                </div>
              </div>
            )}

            {(method === "debt" || remaining > 0) && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input
                  value={debtorName}
                  onChange={(e) => setDebtorName(e.target.value)}
                  placeholder={t("debtorName")}
                  className="h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#071A33]"
                />
                <input
                  value={debtorPhone}
                  onChange={(e) => setDebtorPhone(e.target.value)}
                  placeholder={t("phone")}
                  className="h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#071A33]"
                />
              </div>
            )}

            <MotionButton
              data-testid="confirm-payment"
              onClick={submit}
              disabled={submitting}
              success={payDelight > 0}
              className="w-full h-14 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white font-bold text-base disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Printer className="w-5 h-5" />
              {t("receivePayment")} • {money(total)}
            </MotionButton>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, red }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`tabular font-medium ${red ? "text-rose-600" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}
